/** Condition-filter plumbing for the Management tables.
 *
 * ⛔ DERIVED FROM THE COLUMNS, NOT HAND-WRITTEN PER TABLE.
 * Every Management column already carries `sort: (row) => string | number` — a
 * value accessor. Reusing it means a column that can be sorted can be filtered,
 * so all six tables gained the builder at once and a NEW column is filterable
 * the moment it is added. A per-table field config would have been six lists to
 * keep in sync with the columns beside them, and the seventh table would have
 * shipped without one.
 *
 * ⚠ The inline ConditionFilter path is AND-joined. The Management table
 * FilterBuilder (monday.com formula) uses one global And/Or conjunction.
 */
import type {
  FilterCondition,
  FilterFieldConfig,
  OperatorOption,
} from '@/components/shared/ConditionFilter/types';
import type { FilterColumn } from '@/components/ui/data-table';

import type { ManagementColumn } from './ManagementTable';

/** Cardinality above which a value select becomes unusable and we fall back to
 *  free text. 5,212 keywords must not render 5,212 options. */
const MAX_SELECT_OPTIONS = 25;

const TEXT_OPS: OperatorOption[] = [
  { value: 'contains', label: 'contains' },
  { value: 'not_contains', label: 'does not contain' },
  { value: 'is', label: 'is' },
  { value: 'is_not', label: 'is not' },
  { value: 'empty', label: 'is empty' },
  { value: 'not_empty', label: 'is not empty' },
];

const NUMBER_OPS: OperatorOption[] = [
  { value: 'gt', label: 'greater than' },
  { value: 'lt', label: 'less than' },
  { value: 'gte', label: 'at least' },
  { value: 'lte', label: 'at most' },
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'does not equal' },
];

/** A column is filterable when it has `filter` or `sort` — same accessor
 *  unless `filter` is set (e.g. sort by rank, filter by label). */
export function filterFieldsFromColumns<T>(
  columns: ManagementColumn<T>[],
  rows: T[],
): FilterFieldConfig[] {
  return columns
    .filter((c) => typeof (c.filter ?? c.sort) === 'function')
    .map((c) => {
      const accessor = (c.filter ?? c.sort)!;
      const sample = rows.length ? accessor(rows[0]) : '';
      const numeric = typeof sample === 'number';
      if (numeric) {
        return {
          key: c.key,
          label: c.header,
          operators: NUMBER_OPS,
          valueInput: { kind: 'text' as const, placeholder: 'e.g. 100' },
        };
      }
      // ⚠ Distinct values come from EVERY row, not the current page. Filtering
      // to options drawn from page 1 would silently hide the rest.
      const distinct = Array.from(
        new Set(rows.map((r) => String(accessor(r) ?? '')).filter(Boolean)),
      ).sort();
      return {
        key: c.key,
        label: c.header,
        operators: TEXT_OPS,
        valueInput:
          distinct.length > 0 && distinct.length <= MAX_SELECT_OPTIONS
            ? {
                kind: 'select' as const,
                options: distinct.map((v) => ({ value: v, label: v })),
                placeholder: 'Choose…',
                searchable: distinct.length > 8,
              }
            : { kind: 'text' as const, placeholder: 'Type a value…' },
      };
    });
}

/** Monday-style FilterBuilder columns from the same Management column accessors. */
export function filterColumnsFromColumns<T>(
  columns: ManagementColumn<T>[],
  rows: T[],
): FilterColumn<T>[] {
  return columns
    .filter((c) => typeof (c.filter ?? c.sort) === 'function')
    .map((c) => {
      const accessor = (c.filter ?? c.sort)!;
      const sample = rows.length ? accessor(rows[0]) : '';
      if (typeof sample === 'number') {
        return {
          key: c.key,
          label: c.header,
          type: 'number' as const,
          accessor,
        };
      }
      const distinct = Array.from(
        new Set(rows.map((r) => String(accessor(r) ?? '')).filter(Boolean)),
      ).sort();
      if (distinct.length > 0 && distinct.length <= MAX_SELECT_OPTIONS) {
        return {
          key: c.key,
          label: c.header,
          type: 'enum' as const,
          accessor: (row: T) => String(accessor(row) ?? ''),
          options: distinct.map((v) => ({ value: v, label: v })),
        };
      }
      return {
        key: c.key,
        label: c.header,
        type: 'text' as const,
        accessor: (row: T) => String(accessor(row) ?? ''),
      };
    });
}

function matches(raw: string | number, operator: string, value: string): boolean {
  // ⚠ "is empty" must be evaluated BEFORE the value guard below, or a condition
  // with no typed value would be treated as inactive and silently ignored.
  const asText = String(raw ?? '').trim();
  if (operator === 'empty') return asText === '';
  if (operator === 'not_empty') return asText !== '';

  if (value.trim() === '') return true; // incomplete condition: not yet a filter

  if (typeof raw === 'number') {
    // ⚠ Number('') is 0, NOT NaN. Stripping "abc" leaves an empty string, so a
    // NaN check alone turned a typo into a "> 0" filter that quietly hid rows.
    // Caught by test, not by review.
    const digits = value.replace(/[^0-9.-]/g, '');
    const n = digits === '' ? Number.NaN : Number(digits);
    if (Number.isNaN(n)) return true; // unparseable: do not silently drop rows
    switch (operator) {
      case 'gt': return raw > n;
      case 'lt': return raw < n;
      case 'gte': return raw >= n;
      case 'lte': return raw <= n;
      case 'eq': return raw === n;
      case 'neq': return raw !== n;
      default: return true;
    }
  }

  const hay = asText.toLowerCase();
  const needle = value.trim().toLowerCase();
  switch (operator) {
    case 'contains': return hay.includes(needle);
    case 'not_contains': return !hay.includes(needle);
    case 'is': return hay === needle;
    case 'is_not': return hay !== needle;
    default: return true;
  }
}

/** True when the row satisfies every condition (AND). */
export function rowMatchesConditions<T>(
  row: T,
  conditions: FilterCondition[],
  columns: ManagementColumn<T>[],
): boolean {
  for (const c of conditions) {
    const col = columns.find((x) => x.key === c.field);
    const accessor = col?.filter ?? col?.sort;
    if (!accessor) continue;
    if (!matches(accessor(row), c.operator, c.value)) return false;
  }
  return true;
}
